<template>
  <div class="essays-table" data-testid="essays-table">
    <essays-table-header
      :selected-ids="selectedIds"
      :selected-all="selectedAll"
      @reset-selected="selectedIds = []"
      @delete="$emit('delete', $event)"
      @export="showExportModal"
      @submission-added="$emit('getData')"
    />

    <essays-table-filters
      :total="total"
      :limit="limit"
      :selected-ids="selectedIds"
      :available-filters="availableFilters"
      :selected-all="selectedAll"
      :items-to-select-counts="essays.filter((item) => !isGrading(item)).length"
      :is-filtered="isFiltered"
      data-testid="essays-table-filters"
      @filter-changed="$emit('getData', $event)"
      @export="showExportModal"
      @delete="$emit('delete', $event)"
      @selectedAll="$emit('selectedAll', $event)"
    />

    <eg-selectable-table
      v-model="selectedIds"
      class="essay-table w-100"
      selectable
      split-select
      :items="essays"
      :fields="fields"
      :total="total"
      :limit="limit"
      :current-page="currentPage"
      :get-disabled="isGrading"
      :loading="tableLoading"
      :initial-loading="initialLoading"
      :click-action="openEssay"
      data-testid="eg-selectable-table"
      :show-pagination="showPagination"
      @onChangePage="$emit('onChangePage', $event)"
    >
      <template #empty-state>
        <empty-state
          v-if="!tableLoading && isFiltered && !essays.length"
          key="filtered"
          :title="$t('essays.noEssaysFound')"
          :description="$t('essays.tryAgainOrClear')"
          class="m-auto"
          data-testid="empty-state-filtered"
        >
        </empty-state>
        <empty-state
          v-if="!tableLoading && !isFiltered && !essays.length"
          key="empty"
          :title="$t('essays.noSubmissionsYet')"
          class="m-auto"
          data-testid="empty-state"
        >
          <template #descriptionSlot>
            <div v-if="!features.bulkImports">
              {{ $t("essays.startGrading") }}
            </div>
            <div v-else>{{ $t("essays.syncInProgress") }}</div>
          </template>
          <!--<img
            style="max-height: 230px"
            src="../../assets/illustrations/empty-state-essays.svg"
            alt="EssayGrader Empty State"
          />-->
          <!-- <template #buttons>
            <eg-button
              type="primary"
              icon="plus-add-more-maximize"
              @click="onAddSubmissions"
            >
              Add Submissions
            </eg-button>
          </template>-->
        </empty-state>
      </template>
      <template #head()="{ label, field }">
        <div
          class="d-flex align-items-center cursor-pointer"
          @click.prevent="onSort(field)"
        >
          {{ label }}
          <eg-icon
            v-if="field.egSortable"
            :style="{ color: sortBy === field.key ? '#343A40' : 'unset' }"
            :icon="
              sortBy === field.key
                ? `arrow-${sortDesc ? 'down' : 'up'}`
                : 'arrow-down'
            "
            data-testid="sort-icon"
          />
        </div>
      </template>
      <template #head(flags)="{ label }">
        <div class="d-flex align-items-center">
          {{ label }}
          <eg-icon
            v-b-tooltip.hover
            class="ml-1"
            :title="$t('essays.flagsDescription')"
            icon="information"
          />
        </div>
      </template>
      <template #head(actions)="{ label }">
        {{ label }}
      </template>
      <template #table-colgroup="{ fields }">
        <col
          v-for="field in fields"
          :key="field.key"
          :class="field.columnClass"
          data-testid="table-colgroup"
        />
      </template>

      <template #cell(status)="{ item }">
        <cell-status
          :class="`${(item.status || '').toLowerCase()}-status`"
          :item="item"
          :is-grading="isGrading(item)"
          data-testid="cell-status"
        />
      </template>
      <template #cell(name)="{ item }">
        <cell-name
          :item="item"
          :is-grading="isGrading(item)"
          :is-failed="isFailed(item)"
          :is-native="isNative"
          data-testid="cell-name"
          @add-student="openStudentSelector($event)"
          @edit-student="openStudentSelector($event)"
          @detach-student="onDetachStudent"
        />
      </template>
      <template #cell(grade)="{ item }">
        <cell-grade :item="item" data-testid="cell-grade" />
      </template>
      <template #cell(flags)="{ item }">
        <cell-flags :item="item" data-testid="cell-flags" />
      </template>
      <!-- <template #cell(Rubric)="{ item }">
        <span v-if="item.Rubric" class="small">
          <a
            :href="`/rubric/edit/${item.Rubric.id}?type=preview`"
            class="link"
            data-testid="rubric-link"
          >
            {{ item.Rubric.name || `#${item.Rubric.id}` }}
          </a>
        </span>
        <span v-else-if="item.type" class="small">
          {{ formatDefaultType(item) }}
        </span>
        <span v-else class="text-gray small">None</span>
      </template>
      <template #cell(Assignment)="{ item }">
        <span v-if="item.Assignment" class="small">
          <a
            :href="`/assignment/edit/${item.Assignment.id}`"
            class="link"
            data-testid="assignment-link"
          >
            {{ item.Assignment.title || `#${item.Assignment.id}` }}
          </a>
        </span>
        <span v-else class="text-gray small">None</span>
      </template>
      <template #cell(Class)="{ item }">
        <span v-if="item.Class" class="small">
          <a
            :href="`/class/${item.Class.id}`"
            class="link"
            data-testid="class-link"
          >
            {{ item.Class.name }}
          </a>
        </span>
        <span v-else class="text-gray small">None</span>
      </template> -->
      <template #cell(actions)="{ item }">
        <div class="d-flex align-items-center justify-content-end">
          <eg-button
            v-if="isOpenableItem(item)"
            icon="read-only-view"
            icon-color="#1C0F30"
            size="extra-small"
            data-testid="view-essay"
            @click="openEssay(item)"
          />
          <essay-actions-dropdown
            v-if="!isGrading(item)"
            :essay="item"
            data-testid="essay-actions-dropdown"
            @delete="$emit('delete', $event)"
            @regrade="regradeHandler"
            @export="showExportModal"
            @rename="onRename"
          />
        </div>
      </template>
    </eg-selectable-table>

    <!-- <essay-regrade-modal
      v-if="selectedEssay"
      :essay="selectedEssay"
      data-testid="essay-regrade-modal"
      @finishRegrade="$emit('getData')"
    /> -->

    <students-sidebar
      v-if="isNative"
      v-model="studentSidebarVisible"
      :essay="studentSelectorEssay"
      @studentAssigned="$emit('getData')"
      @studentUpdated="$emit('getData')"
    />

    <essay-rename-modal
      :essay="selectedEssay"
      data-testid="essay-rename-modal"
      @hide="selectedEssay = null"
      @updated="$emit('getData')"
    />

    <export-to-canvas-modal
      :selected-all="selectedAll"
      :selected-essays="
        selectedAll ? [] : selectedEssays.filter((essay) => !isFailed(essay))
      "
      :export-filters="filters"
      data-testid="export-to-canvas-modal"
      @hide="selectedEssays = []"
      @essays-exported="onEssaysExported"
    />

    <export-to-classroom-modal
      :selected-all="selectedAll"
      :selected-essays="
        selectedAll ? [] : selectedEssays.filter((essay) => !isFailed(essay))
      "
      :export-filters="filters"
      data-testid="export-to-classroom-modal"
      @hide="selectedEssays = []"
      @essays-exported="onEssaysExported"
    />

    <export-to-pdf-modal
      :selected-all="selectedAll"
      :selected-ids="
        selectedAll
          ? []
          : selectedEssays
              .filter((essay) => !isFailed(essay))
              .map((essay) => essay.id)
      "
      :filters="filters"
      :merged-pdf="mergedPdf"
      :platform="$route.params.workspaceKey"
      data-testid="export-to-pdf-modal"
      @hide="selectedEssays = []"
    />

    <export-to-csv-modal
      :selected-all="selectedAll"
      :selected-ids="
        selectedAll
          ? []
          : selectedEssays
              .filter((essay) => !isFailed(essay))
              .map((essay) => essay.id)
      "
      :filters="filters"
      data-testid="export-to-csv-modal"
      @hide="selectedEssays = []"
    />
  </div>
</template>

<script>
import { getIntensityDisplayText, formatDefaultType } from "@/utils/format";
import CellStatus from "@/components/essay/table/cell_status.vue";
import CellName from "@/components/essay/table/cell_name.vue";
import CellGrade from "@/components/essay/table/cell_grade.vue";
import CellFlags from "@/components/essay/table/cell_flags.vue";
import EgSelectableTable from "@/components/global/eg_selectable_table";
import EssayActionsDropdown from "./essay_actions_dropdown.vue";
import EssayRegradeModal from "./essay_regrade_modal.vue";
import EssayRenameModal from "@/components/essay/modals/essay_rename_modal.vue";
import EssaysTableFilters from "./filters/essays_table_filters.vue";
import EmptyState from "../global/empty_state.vue";
import regradeMixin from "@/mixins/regradeMixin";
import ExportToClassroomModal from "@/components/essay/modals/export_to_classroom.vue";
import ExportToCanvasModal from "@/components/essay/modals/export_to_canvas.vue";
import ExportToPdfModal from "@/components/essay/modals/export_to_pdf.vue";
import ExportToCsvModal from "@/components/essay/modals/export_to_csv.vue";
import EssaysTableHeader from "./essays_table_header.vue";
import StudentsSidebar from "@/components/students/students_sidebar.vue";
import formatDate from "@/utils/formatDate";
import { onAddSubmissions } from "./essays_table_utils";
import { detachStudentFromEssay } from "@/api/requests/students";
import { mapGetters } from "vuex";
import i18n from "@/i18n/i18n";

const getDefaultFields = () => [
  {
    key: "name",
    label: i18n.t("common.assignment"),
    tdClass: "text-truncate",
    columnClass: "col-name",
    egSortable: true,
  },
  {
    key: "flags",
    label: i18n.t("essays.plagiarism"),
    columnClass: "col-flags",
    egSortable: false,
  },
  {
    key: "createdAt",
    label: i18n.t("common.date"),
    columnClass: "col-grade-date",
    egSortable: true,
    formatter: (value) => {
      return formatDate(value);
    },
  },
  {
    key: "grade",
    label: i18n.t("common.grade"),
    headerClass: "col-grade",
    columnClass: "col-grade",
    egSortable: true,
  },
  {
    key: "status",
    label: i18n.t("common.status"),
    columnClass: "col-status",
    egSortable: true,
  },
  {
    key: "actions",
    label: "",
    tdClass: "text-right",
    columnClass: "col-actions",
  },
];

export default {
  name: "EssaysTable",
  components: {
    EgSelectableTable,
    EssayActionsDropdown,
    EssayRenameModal,
    // EssayRegradeModal,
    EssaysTableFilters,
    EmptyState,
    CellStatus,
    CellName,
    CellGrade,
    CellFlags,
    ExportToClassroomModal,
    ExportToCanvasModal,
    ExportToPdfModal,
    ExportToCsvModal,
    EssaysTableHeader,
    StudentsSidebar,
  },

  mixins: [regradeMixin],

  props: {
    essays: {
      type: Array,
      required: true,
    },
    availableFilters: {
      type: Array,
      default: () => [
        "reviewStatus",
        "isExported",
        "gradeRange",
        "flags",
        "createdAt",
        "studentId",
      ],
    },
    fields: {
      type: Array,
      required: false,
      default: () => getDefaultFields(),
    },
    total: {
      type: Number,
      required: true,
    },
    limit: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    selectedAll: {
      type: Boolean,
      required: true,
    },
    sortBy: {
      type: String,
      default: "",
    },
    sortDesc: {
      type: Boolean,
      required: false,
      default: false,
    },
    tableLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    isFiltered: {
      type: Boolean,
      default: false,
    },
    showPagination: {
      type: Boolean,
      default: false,
    },
    isNative: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedIds: [],
      selectedEssays: [],
      selectedEssay: null,
      mergedPdf: false,
      studentSelectorEssay: null,
      studentSidebarVisible: false,
    };
  },

  computed: {
    ...mapGetters("config", ["features"]),
    folderId() {
      return this.$route.params.id;
    },
  },

  watch: {
    isNative(val) {
      if (val) this.$store.dispatch("students/reload");
    },
    "selectedIds.length"() {
      this.$emit("selectedAll", false);
    },
  },

  created() {
    if (this.isNative) {
      this.$store.dispatch("students/load");
    }

    this.$watch(
      () => {
        return this.total + this.limit + this.currentPage;
      },
      () => {
        this.selectedIds = [];
      }
    );

    this.$eventBus.$on("update-essays-table", this.onUpdateEssaysTable);
  },

  beforeDestroy() {
    this.$eventBus.$off("update-essays-table", this.onUpdateEssaysTable);
  },

  methods: {
    onSort(field) {
      const sortDesc = field.key === this.sortBy ? !this.sortDesc : false;

      this.$emit("getData", {
        sortBy: field.key,
        sortDesc: sortDesc,
      });
    },

    onUpdateEssaysTable({ folderId, workspaceKey }) {
      if (
        workspaceKey === this.$route.params.workspaceKey &&
        +folderId === +this.folderId
      ) {
        this.$emit("getData");
      }
    },

    showExportModal({ type, essay, mergedPdf }) {
      this.selectedEssays = essay
        ? [essay]
        : this.selectedIds.map((id) => this.essays.find((e) => e.id === id));

      this.mergedPdf = mergedPdf;

      this.$nextTick(() => {
        this.$bvModal.show(`export-to-${type}-modal`);
      });
    },
    onRename(essay) {
      this.selectedEssay = essay;
      this.$nextTick(() => {
        this.$bvModal.show("essay-rename-modal");
      });
    },

    async regradeHandler(essay) {
      this.selectedEssay = essay;
      const regraded = await this.$egModal.show("essay-regrade-modal", {
        component: EssayRegradeModal,
        props: {
          essay,
        },
      });
      // const regraded = await this.regrade(essay);
      regraded && this.$emit("getData");
    },
    isGrading(item) {
      return item.status === "Queued";
    },

    isFailed(item) {
      return item.status === "Failed";
    },

    getIntensityDisplayText(intensity) {
      return getIntensityDisplayText(intensity);
    },

    formatDefaultType(essay) {
      return formatDefaultType(essay);
    },

    isOpenableItem(item) {
      return !this.isGrading(item) && !this.isFailed(item);
    },

    openEssay(item) {
      // cannot open failed or queued essays
      if (!this.isOpenableItem(item)) {
        return;
      }

      this.$router.push({
        name: "workspace-essay-view",
        params: {
          workspaceKey: this.$route.params.workspaceKey,
          id: this.folderId,
          essayId: item.id,
        },
      });
    },

    async onDetachStudent(essay) {
      const studentName =
        `${essay.Student.firstName} ${essay.Student.lastName}`.trim();
      const confirmed = await this.$showDeleteConfirmationModal({
        title: this.$t("common.removeStudentTitle", { name: studentName }),
        message: this.$t("essays.detachStudentMessage", {
          name: studentName,
          essayName: essay.name,
        }),
      });
      if (!confirmed) return;
      try {
        await detachStudentFromEssay({
          studentId: essay.Student.id,
          essayId: essay.id,
        });
        this.$emit("getData");
      } catch (e) {
        this.$showToastError(e.response?.data?.error);
      }
    },

    openStudentSelector(essay) {
      this.studentSelectorEssay = essay;
      this.studentSidebarVisible = true;
    },

    async onAddSubmissions() {
      await onAddSubmissions({
        workspaceKey: this.$route.params.workspaceKey,
        folderId: +this.folderId,
      });

      this.$emit("getData");
    },

    onEssaysExported() {
      this.$emit("getData");
    },
  },
};
</script>

<style lang="scss" scoped>
.link {
  color: #343a40;
}

.essays-table {
  position: relative;
  top: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 100%;
  border-radius: var(--radius-rd-400, 12px);
  border: 1px solid var(--color-stroke-neutral-default);
  border-top: none;
  .essays-table-filters,
  .essays-table-header {
    position: sticky;
    z-index: 4;
    background: var(--color-surface-neutral-background, #fff);
  }
  .essays-table-header {
    top: 0;
    z-index: 5;
  }
  .essay-table {
    background-color: var(--color-surface-neutral-background, #fff);
  }

  &::v-deep {
    --table-height: 100%;
    --min-table-height: 350px;
    --table-header-height: 48px;
    --max-row-height: calc(
      var(--table-height) - var(--table-header-height) - 2px
    );
    .essays-table-header {
      border-top-left-radius: var(--radius-rd-400, 12px);
      border-top-right-radius: var(--radius-rd-400, 12px);
      border-top: 1px solid var(--color-stroke-neutral-default);
    }
    .b-table-sticky-header {
      max-height: 100%;
      min-height: var(--min-table-height);
      height: var(--table-height);
      margin-bottom: 0;
      overflow: unset;
    }

    .b-table-sticky-header > .table.b-table > thead > tr > th {
      top: calc(55px + 68px);
    }

    .b-table-bottom-row > td {
      height: var(--max-row-height);
    }

    .selectable-table-wrapper {
      height: var(--table-height);
      display: flex;
    }

    .selectable-table {
      width: 100%;
      display: flex;
      flex-direction: column;
      margin-top: 0 !important;
      border-top: none;
      border-radius: 0.25rem;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      /* TODO Correct calculate height of table */
      height: var(--table-height);
      min-height: var(--min-table-height);

      @media (max-width: 768px) {
        border-top: 1px solid hsl(0deg, 0%, 87%);
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
      }

      &:not(:has(.pagination)) {
        & .table tr.b-table-bottom-row {
          border-top: 1px solid #dee2e6;
          border-bottom: none;
          height: 48px;
        }
      }

      & .table tr:has(.queued-status),
      & .table tr:has(.failed-status) {
        cursor: default;
      }

      & .table tr td {
        padding-top: 16px;
        padding-bottom: 16px;
        vertical-align: middle;
        color: var(--color-text-neutral-on-white, #1c0f30);
        font-family: var(--typography-font-family-text-primary, Inter);
        font-size: var(--typography-font-size-fs-200, 12px);
        font-style: normal;
        font-weight: var(--typography-font-weight-fw-600, 600);
        line-height: 120%; /* 14.4px */
      }

      & .table tr.b-table-top-row td {
        padding: 0;
      }

      & .table tr th {
        vertical-align: middle;
        background: var(--color-surface-neutral-container-1, #f6f8fa);
        color: var(--color-text-neutral-secondary, #566370);
        font-family: var(--typography-font-family-text-primary, Inter);
        font-size: var(--typography-font-size-fs-200, 12px);
        font-style: normal;
        font-weight: var(--typography-font-weight-fw-600, 600);
        line-height: 120%; /* 14.4px */
      }

      .custom-control.custom-checkbox {
        // min-height: 2.25rem;
        .custom-control-input {
          height: 3rem;
          width: 2.5rem;
          top: -20%;
          left: -43%;
          cursor: pointer;
        }
        .custom-control-label {
          cursor: pointer;
        }
        input:hover + .custom-control-label:before {
          border-color: var(--purple-600);
        }
        input:focus + .custom-control-label:before {
          box-shadow: 0 0 0 0.2rem var(--purple-100);
        }
      }
    }
    .empty-state {
      height: 40vh;
    }
    @media (max-width: 768px) {
      .selectable-table .table tr th:nth-child(4),
      .selectable-table .table tr td:nth-child(4),
      .selectable-table .table tr th:nth-child(5),
      .selectable-table .table tr td:nth-child(5),
      .selectable-table .table tr th:nth-child(6),
      .selectable-table .table tr td:nth-child(6),
      .selectable-table .table tr th:nth-child(7),
      .selectable-table .table tr td:nth-child(7) {
        display: none;
      }
    }
  }
}

.badge-pill {
  width: 83px;
  color: white;
  text-align: left;

  svg {
    margin-right: 3px;
  }
}

.badge-cyan {
  background-color: #17a2b8;
}

.badge-secondary {
  background-color: #6c757d;
}

.col-select {
  width: 48px;
}

.col-grade,
.col-status {
  width: 135px;
}

.col-grade-date {
  width: 170px;
}

.col-name {
  max-width: 50%;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 70%;
  }
}

.col-flags,
.col-actions {
  width: 38px;

  @media (min-width: 992px) {
    width: 70px;
  }
}

.skeleton_loading_bar {
  background-color: rgba(0, 0, 0, 0.25);
  height: 20px;
}

.text-gray {
  color: var(--gray, #6c757d);
}

.essay-skeleton-details {
  height: 16px;
}
</style>
